'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth/auth-context';
import { 
  hasVendorAccess, 
  getAccessibleVendors, 
  getUserTeamInfo, 
  hasVendorPermission, 
  getVendorFeatureAccess,
  isVendorAdmin
} from '@/lib/auth/vendor-access';
import { Permission } from '@/lib/permissions/team-permissions';

/**
 * Hook to check if the current user has access to a specific vendor's data
 * and to get all vendors the user has access to
 */
export function useVendorAccess() {
  const { user, isAdmin } = useAuth();
  const [accessibleVendors, setAccessibleVendors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load all accessible vendors on mount
  useEffect(() => {
    async function loadAccessibleVendors() {
      if (!user) {
        setAccessibleVendors([]);
        setIsLoading(false);
        return;
      }

      try {
        const vendorIds = await getAccessibleVendors(user.id);
        setAccessibleVendors(vendorIds);
      } catch (error) {
        console.error('Error loading accessible vendors:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadAccessibleVendors();
  }, [user]);

  // Check if user has access to a specific vendor
  const checkAccess = useCallback(
    async (vendorId: string): Promise<boolean> => {
      if (!user) return false;
      
      // Admin has access to all vendors
      if (isAdmin) return true;
      
      // Quick check if vendorId is in the already loaded accessible vendors
      if (accessibleVendors.includes(vendorId)) return true;
      
      // If not found in cached list, check directly
      return hasVendorAccess(user.id, vendorId);
    },
    [user, isAdmin, accessibleVendors]
  );

  // Check if the current user is a member of a vendor team (includes profile vendor_id and team membership)
  const isVendorTeamMember = useCallback(
    async (vendorId: string): Promise<boolean> => {
      if (!user) return false;
      
      // Quick check: if user has vendor_id in profile
      if (user.vendorId === vendorId) return true;
      
      // Check actual team membership
      const teamInfo = await getUserTeamInfo(user.id, vendorId);
      return teamInfo?.isTeamMember || false;
    },
    [user]
  );

  // Check if the current user is the owner of a vendor (user_id matches vendor's user_id)
  const isVendorOwner = useCallback(
    async (vendorId: string): Promise<boolean> => {
      if (!user) return false;
      
      // If user is already known to be the vendor (from auth context)
      if (user.vendorInfo && user.vendorInfo.id === vendorId) return true;
      
      // Otherwise check directly with the database
      const teamInfo = await getUserTeamInfo(user.id, vendorId);
      return teamInfo?.isOwner || false;
    },
    [user]
  );

  // Check if user has admin privileges for vendor (owner or team admin)
  const isVendorAdminUser = useCallback(
    async (vendorId: string): Promise<boolean> => {
      if (!user) return false;
      if (isAdmin) return true;
      
      return isVendorAdmin(user.id, vendorId);
    },
    [user, isAdmin]
  );

  // Check if user has a specific permission for vendor
  const checkVendorPermission = useCallback(
    async (vendorId: string, permission: Permission): Promise<boolean> => {
      if (!user) return false;
      if (isAdmin) return true;
      
      return hasVendorPermission(user.id, vendorId, permission);
    },
    [user, isAdmin]
  );

  // Get feature access levels for vendor
  const getFeatureAccess = useCallback(
    async (vendorId: string, feature: 'orders' | 'inventory' | 'events' | 'payments' | 'products' | 'team' | 'settings' | 'reports') => {
      if (!user) return { canView: false, canManage: false };
      if (isAdmin) return { canView: true, canManage: true };
      
      return getVendorFeatureAccess(user.id, vendorId, feature);
    },
    [user, isAdmin]
  );

  // Get user's team information for vendor
  const getTeamInfo = useCallback(
    async (vendorId: string) => {
      if (!user) return null;
      
      return getUserTeamInfo(user.id, vendorId);
    },
    [user]
  );

  return {
    checkAccess,
    accessibleVendors,
    isVendorTeamMember,
    isVendorOwner,
    isVendorAdmin: isVendorAdminUser,
    checkVendorPermission,
    getFeatureAccess,
    getTeamInfo,
    isLoading,
  };
}
